import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { CircularProgress } from "@mui/material";
import PaidIcon from "@mui/icons-material/Paid";
import { toast } from "react-toastify";
import * as S from "./styles";
import { formatDate } from "../../utils/formatDatas";

export interface IReceiptData {
  nameReceiver: string;
  codeReceiver: number | string;
  amount: number | string;
  date: Date | string;
  nameSender?: string;
}

interface IModalReceipt {
  open: boolean;
  onClose: () => void;
  data: IReceiptData | null;
}

type Busy = "share" | "download" | null;

const ModalReceipt: React.FC<IModalReceipt> = ({ open, onClose, data }) => {
  const receiptRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState<Busy>(null);

  // mantém o último recibo durante a animação de fechar o painel
  const lastRef = useRef<IReceiptData | null>(null);
  if (data) lastRef.current = data;
  const current = data ?? lastRef.current;

  // Gera a imagem do comprovante (usada no salvar e no compartilhar)
  const generateImage = async (): Promise<HTMLCanvasElement | null> => {
    if (!receiptRef.current) return null;
    try {
      return await html2canvas(receiptRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
    } catch (error) {
      console.error("Erro ao gerar canvas do comprovante:", error);
      toast.error("Não foi possível gerar a imagem do comprovante.");
      return null;
    }
  };

  const download = (canvas: HTMLCanvasElement) => {
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `mutattio_coins_comprovante_${new Date().getTime()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = async () => {
    setBusy("download");
    const canvas = await generateImage();
    if (canvas) download(canvas);
    setBusy(null);
  };

  // Compartilhamento nativo (Web Share API); sem suporte, baixa a imagem
  const handleShare = async () => {
    setBusy("share");
    const canvas = await generateImage();
    if (!canvas) {
      setBusy(null);
      return;
    }

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png"),
    );
    if (!blob) {
      setBusy(null);
      return;
    }

    const file = new File([blob], `comprovante_${new Date().getTime()}.png`, {
      type: "image/png",
    });

    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          title: "Comprovante Mutattio Coins",
          text: `Comprovante de transferência de ${current?.amount} moedas para ${current?.nameReceiver}.`,
          files: [file],
        });
      } catch (error) {
        // cancelar o menu de compartilhamento não é erro
        console.log("Compartilhamento cancelado ou falhou:", error);
      }
    } else {
      download(canvas);
      toast.info(
        "Seu navegador não compartilha arquivos. O comprovante foi baixado.",
      );
    }
    setBusy(null);
  };

  return (
    <S.Sheet
      anchor="bottom"
      open={open && !!data}
      onOpen={() => undefined}
      onClose={busy ? () => undefined : onClose}
      disableSwipeToOpen
    >
      <S.Handle />
      {current && (
        <>
          <S.ReceiptContainer ref={receiptRef}>
            <S.HeaderReceipt>
              <S.Image />
              <S.SuccessIcon />
              <S.Title>Transferência Realizada</S.Title>
              <S.DateText>{formatDate(String(current.date))}</S.DateText>
            </S.HeaderReceipt>

            <S.ValueHighlight>
              <PaidIcon style={{ fontSize: 36, color: "#f37214" }} />
              {current.amount}
            </S.ValueHighlight>

            <S.Divider />

            {current.nameSender && (
              <S.DataRow>
                <S.Label>Origem:</S.Label>
                <S.Value>{current.nameSender}</S.Value>
              </S.DataRow>
            )}

            <S.DataRow>
              <S.Label>Destino:</S.Label>
              <S.Value>{current.nameReceiver}</S.Value>
            </S.DataRow>

            <S.DataRow>
              <S.Label>Código:</S.Label>
              <S.Value>mutattio/{current.codeReceiver}</S.Value>
            </S.DataRow>

            <S.Divider
              style={{
                marginBottom: 0,
                borderBottomStyle: "solid",
                borderBottomWidth: 4,
                borderBottomColor: "#f5f5f5",
              }}
            />
          </S.ReceiptContainer>

          <S.ActionContainer>
            <S.RowButtons>
              <S.ShareButton
                onClick={handleShare}
                disabled={!!busy}
                disableElevation
              >
                {busy === "share" ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <>
                    <S.ShrIcon />
                    Compartilhar
                  </>
                )}
              </S.ShareButton>

              <S.DownloadButton
                onClick={handleDownload}
                disabled={!!busy}
                disableElevation
              >
                {busy === "download" ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <>
                    <S.DownIcon />
                    Salvar
                  </>
                )}
              </S.DownloadButton>
            </S.RowButtons>

            <S.CloseButton onClick={onClose} disabled={!!busy}>
              Fechar
            </S.CloseButton>
          </S.ActionContainer>
        </>
      )}
    </S.Sheet>
  );
};

export default ModalReceipt;
